import { create } from 'zustand';
import {
    getMerchantKey,
    hasMerchantName,
    Merchant,
    MerchantWorkflow,
} from '../../../types/onboarding/Merchant';
import { MerchantBankAccount } from '../../../types/onboarding/MerchantBankAccount';
import { OnboardedStore } from '../../../types/onboarding/OnboardStore';
import { MerchantPriceList, StorePriceSyncResult } from '../../../types/onboarding/MerchantPrices';
import { RegisteredSignatory } from '../../../types/onboarding/MerchantAgreement';
import ResponseError from '../../../types/response/ResponseError';
import { ErrorType } from '../../../types/response/ErrorCodes';
import { formatOrganizationNumber } from '../../../services/helpers/organizationNumber';

/** The merchant as named in messages: its name, or the formatted organization number until KYC has set the name. */
const describeMerchant = (merchant: Merchant): string =>
    hasMerchantName(merchant)
        ? merchant.name
        : formatOrganizationNumber(merchant.countryCode, merchant.organizationNumber);

/** Why a workflow could not be opened, together with how the error is shown in the merchants list. */
export interface WorkflowError {
    workflow: MerchantWorkflow;
    error: ResponseError;
    /** Error header. */
    errorType: ErrorType;
    /** data-testid of the error, e.g. kyc-merchant-error. */
    testId: string;
}

/** Name of each workflow, as in the "opened in a new tab" message. */
const workflowLabels: Record<MerchantWorkflow, string> = {
    [MerchantWorkflow.Kyc]: 'KYC',
    [MerchantWorkflow.BankAccount]: 'Bank account',
};

/** A workflow opened in a new tab for a merchant. */
export interface OpenedWorkflow {
    workflow: MerchantWorkflow;
    /** Name of the workflow, e.g. "KYC". */
    label: string;
    /** The merchant as named in messages (see describeMerchant). */
    merchantName: string;
}

/** The merchant last added through the add merchant form. */
export interface AddedMerchant {
    merchant: Merchant;
    /** The merchant as named in messages (see describeMerchant). */
    name: string;
}

const workflowErrorPresentations: Record<
    MerchantWorkflow,
    Pick<WorkflowError, 'errorType' | 'testId'>
> = {
    [MerchantWorkflow.Kyc]: {
        errorType: ErrorType.StartKyc,
        testId: 'kyc-merchant-error',
    },
    [MerchantWorkflow.BankAccount]: {
        errorType: ErrorType.StartBankAccountVerification,
        testId: 'bank-account-merchant-error',
    },
};

export interface OnboardingStore {
    merchants: Merchant[];
    isLoadingMerchants: boolean;
    merchantsError: ResponseError | undefined;
    /** Verified bank accounts of the merchant identified by bankAccountsMerchantKey (see getMerchantKey). */
    bankAccounts: MerchantBankAccount[];
    bankAccountsMerchantKey: string | undefined;
    isLoadingBankAccounts: boolean;
    bankAccountsError: ResponseError | undefined;
    /** Key (see getMerchantKey) of the merchant whose status is being changed, if any. */
    updatingMerchantKey: string | undefined;
    updateMerchantError: ResponseError | undefined;
    /** The workflow, and the merchant (see getMerchantKey), whose session is being opened, if any. */
    startingWorkflow: { workflow: MerchantWorkflow; merchantKey: string } | undefined;
    /** The workflow last opened in a new tab, until the user returns to Merchant Hub and the merchants are re-fetched. */
    openedWorkflow: OpenedWorkflow | undefined;
    /** Why the last workflow could not be opened, if it failed; cleared when a workflow is opened again. */
    workflowError: WorkflowError | undefined;
    isAddingMerchant: boolean;
    /** The merchant last added through the add merchant form, until the form is cleared or used again. */
    addedMerchant: AddedMerchant | undefined;
    addMerchantError: ResponseError | undefined;
    isOnboardingStore: boolean;
    onboardedStore: OnboardedStore | undefined;
    onboardStoreError: ResponseError | undefined;
    /** Key (see getMerchantKey) of the merchant whose price list is shown, if any. */
    pricesMerchantKey: string | undefined;
    /** The price list of the merchant identified by pricesMerchantKey, as last loaded or saved. */
    priceList: MerchantPriceList | undefined;
    isLoadingPrices: boolean;
    pricesError: ResponseError | undefined;
    isSavingPrices: boolean;
    /** Whether the price list was saved since it was opened or last changed. */
    pricesSaved: boolean;
    savePricesError: ResponseError | undefined;
    isSyncingPrices: boolean;
    /** What the last sync did per store, until the list is saved or closed. */
    priceSyncResults: StorePriceSyncResult[] | undefined;
    syncPricesError: ResponseError | undefined;
    /** Key (see getMerchantKey) of the merchant whose agreement is being sent, if any. */
    agreementMerchantKey: string | undefined;
    /** Who may sign for the merchant identified by agreementMerchantKey, according to the register. */
    signatories: RegisteredSignatory[];
    isLoadingSignatories: boolean;
    signatoriesError: ResponseError | undefined;
    isSendingAgreement: boolean;
    /** Whether the agreement was sent since the modal was opened. */
    agreementSent: boolean;
    sendAgreementError: ResponseError | undefined;
    listMerchantsStarted: () => void;
    listMerchantsSucceeded: (merchants: Merchant[]) => void;
    listMerchantsFailed: (error: ResponseError) => void;
    listBankAccountsStarted: (merchant: Merchant) => void;
    listBankAccountsSucceeded: (merchant: Merchant, bankAccounts: MerchantBankAccount[]) => void;
    listBankAccountsFailed: (error: ResponseError) => void;
    clearBankAccounts: () => void;
    updateMerchantStarted: (merchant: Merchant) => void;
    updateMerchantSucceeded: (merchant: Merchant) => void;
    updateMerchantFailed: (error: ResponseError) => void;
    startWorkflowStarted: (workflow: MerchantWorkflow, merchant: Merchant) => void;
    startWorkflowSucceeded: (workflow: MerchantWorkflow, merchant: Merchant) => void;
    startWorkflowFailed: (workflow: MerchantWorkflow, error: ResponseError) => void;
    clearOpenedWorkflow: () => void;
    addMerchantStarted: () => void;
    addMerchantSucceeded: (merchant: Merchant) => void;
    addMerchantFailed: (error: ResponseError) => void;
    clearAddMerchant: () => void;
    onboardStoreStarted: () => void;
    onboardStoreSucceeded: (store: OnboardedStore) => void;
    onboardStoreFailed: (error: ResponseError) => void;
    clearOnboardStore: () => void;
    loadPricesStarted: (merchant: Merchant) => void;
    loadPricesSucceeded: (merchant: Merchant, priceList: MerchantPriceList) => void;
    loadPricesFailed: (error: ResponseError) => void;
    savePricesStarted: () => void;
    savePricesSucceeded: (priceList: MerchantPriceList) => void;
    savePricesFailed: (error: ResponseError) => void;
    syncPricesStarted: () => void;
    syncPricesSucceeded: (results: StorePriceSyncResult[]) => void;
    syncPricesFailed: (error: ResponseError) => void;
    clearPrices: () => void;
    loadSignatoriesStarted: (merchant: Merchant) => void;
    loadSignatoriesSucceeded: (merchant: Merchant, signatories: RegisteredSignatory[]) => void;
    loadSignatoriesFailed: (error: ResponseError) => void;
    sendAgreementStarted: () => void;
    sendAgreementSucceeded: (merchant: Merchant) => void;
    sendAgreementFailed: (error: ResponseError) => void;
    clearAgreement: () => void;
    reset: () => void;
}

export const initialState = {
    merchants: [] as Merchant[],
    isLoadingMerchants: false,
    merchantsError: undefined,
    bankAccounts: [] as MerchantBankAccount[],
    bankAccountsMerchantKey: undefined,
    isLoadingBankAccounts: false,
    bankAccountsError: undefined,
    updatingMerchantKey: undefined,
    updateMerchantError: undefined,
    startingWorkflow: undefined,
    openedWorkflow: undefined,
    workflowError: undefined,
    isAddingMerchant: false,
    addedMerchant: undefined,
    addMerchantError: undefined,
    isOnboardingStore: false,
    onboardedStore: undefined,
    onboardStoreError: undefined,
    pricesMerchantKey: undefined,
    priceList: undefined,
    isLoadingPrices: false,
    pricesError: undefined,
    isSavingPrices: false,
    pricesSaved: false,
    savePricesError: undefined,
    isSyncingPrices: false,
    priceSyncResults: undefined,
    syncPricesError: undefined,
    agreementMerchantKey: undefined,
    signatories: [] as RegisteredSignatory[],
    isLoadingSignatories: false,
    signatoriesError: undefined,
    isSendingAgreement: false,
    agreementSent: false,
    sendAgreementError: undefined,
};

const initialPricesState = {
    pricesMerchantKey: undefined,
    priceList: undefined,
    isLoadingPrices: false,
    pricesError: undefined,
    isSavingPrices: false,
    pricesSaved: false,
    savePricesError: undefined,
    isSyncingPrices: false,
    priceSyncResults: undefined,
    syncPricesError: undefined,
};

const initialAgreementState = {
    agreementMerchantKey: undefined,
    signatories: [] as RegisteredSignatory[],
    isLoadingSignatories: false,
    signatoriesError: undefined,
    isSendingAgreement: false,
    agreementSent: false,
    sendAgreementError: undefined,
};

export const useOnboardingStore = create<OnboardingStore>(set => ({
    ...initialState,

    listMerchantsStarted: () =>
        set({
            merchants: [],
            isLoadingMerchants: true,
            merchantsError: undefined,
        }),

    listMerchantsSucceeded: (merchants: Merchant[]) =>
        set({
            merchants: merchants,
            isLoadingMerchants: false,
        }),

    listMerchantsFailed: (error: ResponseError) =>
        set({
            merchants: [],
            merchantsError: error,
            isLoadingMerchants: false,
        }),

    listBankAccountsStarted: (merchant: Merchant) =>
        set({
            bankAccounts: [],
            bankAccountsMerchantKey: getMerchantKey(merchant),
            isLoadingBankAccounts: true,
            bankAccountsError: undefined,
        }),

    listBankAccountsSucceeded: (merchant: Merchant, bankAccounts: MerchantBankAccount[]) =>
        set(state =>
            // A slower response for a merchant that is no longer selected must not replace the current accounts.
            state.bankAccountsMerchantKey === getMerchantKey(merchant)
                ? { bankAccounts, isLoadingBankAccounts: false }
                : {}
        ),

    listBankAccountsFailed: (error: ResponseError) =>
        set({
            bankAccounts: [],
            isLoadingBankAccounts: false,
            bankAccountsError: error,
        }),

    clearBankAccounts: () =>
        set({
            bankAccounts: [],
            bankAccountsMerchantKey: undefined,
            isLoadingBankAccounts: false,
            bankAccountsError: undefined,
        }),

    updateMerchantStarted: (merchant: Merchant) =>
        set({
            updatingMerchantKey: getMerchantKey(merchant),
            updateMerchantError: undefined,
        }),

    updateMerchantSucceeded: (merchant: Merchant) =>
        set(state => ({
            merchants: state.merchants.map(existing =>
                getMerchantKey(existing) === getMerchantKey(merchant) ? merchant : existing
            ),
            updatingMerchantKey: undefined,
        })),

    updateMerchantFailed: (error: ResponseError) =>
        set({
            updatingMerchantKey: undefined,
            updateMerchantError: error,
        }),

    startWorkflowStarted: (workflow: MerchantWorkflow, merchant: Merchant) =>
        set({
            startingWorkflow: { workflow, merchantKey: getMerchantKey(merchant) },
            openedWorkflow: undefined,
            workflowError: undefined,
        }),

    startWorkflowSucceeded: (workflow: MerchantWorkflow, merchant: Merchant) =>
        set({
            startingWorkflow: undefined,
            openedWorkflow: {
                workflow,
                label: workflowLabels[workflow],
                merchantName: describeMerchant(merchant),
            },
        }),

    startWorkflowFailed: (workflow: MerchantWorkflow, error: ResponseError) =>
        set({
            startingWorkflow: undefined,
            workflowError: { workflow, error, ...workflowErrorPresentations[workflow] },
        }),

    clearOpenedWorkflow: () =>
        set({
            openedWorkflow: undefined,
        }),

    addMerchantStarted: () =>
        set({
            isAddingMerchant: true,
            addedMerchant: undefined,
            addMerchantError: undefined,
        }),

    // The new merchant is appended right away; a re-fetch would blank the table through listMerchantsStarted.
    // An existing entry with the same key is replaced so the list stays free of duplicates.
    addMerchantSucceeded: (merchant: Merchant) =>
        set(state => ({
            merchants: [
                ...state.merchants.filter(
                    existing => getMerchantKey(existing) !== getMerchantKey(merchant)
                ),
                merchant,
            ],
            isAddingMerchant: false,
            addedMerchant: { merchant, name: describeMerchant(merchant) },
        })),

    addMerchantFailed: (error: ResponseError) =>
        set({
            isAddingMerchant: false,
            addMerchantError: error,
        }),

    clearAddMerchant: () =>
        set({
            isAddingMerchant: false,
            addedMerchant: undefined,
            addMerchantError: undefined,
        }),

    onboardStoreStarted: () =>
        set({
            isOnboardingStore: true,
            onboardedStore: undefined,
            onboardStoreError: undefined,
        }),

    onboardStoreSucceeded: (store: OnboardedStore) =>
        set({
            isOnboardingStore: false,
            onboardedStore: store,
        }),

    onboardStoreFailed: (error: ResponseError) =>
        set({
            isOnboardingStore: false,
            onboardStoreError: error,
        }),

    clearOnboardStore: () =>
        set({
            isOnboardingStore: false,
            onboardedStore: undefined,
            onboardStoreError: undefined,
        }),

    loadPricesStarted: (merchant: Merchant) =>
        set({
            ...initialPricesState,
            pricesMerchantKey: getMerchantKey(merchant),
            isLoadingPrices: true,
        }),

    loadPricesSucceeded: (merchant: Merchant, priceList: MerchantPriceList) =>
        set(state =>
            // A slower response for a merchant whose list is no longer open must not replace the current list.
            state.pricesMerchantKey === getMerchantKey(merchant)
                ? { priceList, isLoadingPrices: false }
                : {}
        ),

    loadPricesFailed: (error: ResponseError) =>
        set({
            isLoadingPrices: false,
            pricesError: error,
        }),

    savePricesStarted: () =>
        set({
            isSavingPrices: true,
            pricesSaved: false,
            savePricesError: undefined,
            priceSyncResults: undefined,
            syncPricesError: undefined,
        }),

    savePricesSucceeded: (priceList: MerchantPriceList) =>
        set({
            priceList,
            isSavingPrices: false,
            pricesSaved: true,
        }),

    savePricesFailed: (error: ResponseError) =>
        set({
            isSavingPrices: false,
            savePricesError: error,
        }),

    // A new save or sync clears the outcome of the earlier one, so only the latest action is reported.
    syncPricesStarted: () =>
        set({
            isSyncingPrices: true,
            pricesSaved: false,
            savePricesError: undefined,
            priceSyncResults: undefined,
            syncPricesError: undefined,
        }),

    // The sync went through only when every store and product took the prices; otherwise it is reported like a refused
    // sync, without the outcome per store.
    syncPricesSucceeded: (results: StorePriceSyncResult[]) =>
        set({
            isSyncingPrices: false,
            priceSyncResults: results,
            syncPricesError: results.some(
                store =>
                    store.outcome === 'Failed' ||
                    store.products.some(product => product.outcome === 'Failed')
            )
                ? new ResponseError(
                      [],
                      'MERCHANT_PRICE_SYNC_INCOMPLETE',
                      'MERCHANT_PRICE_SYNC_INCOMPLETE',
                      'The prices could not be synced to all stores',
                      200,
                      'The prices could not be synced to all stores. Try again, and contact support if the problem remains.',
                      null,
                      {}
                  )
                : undefined,
        }),

    syncPricesFailed: (error: ResponseError) =>
        set({
            isSyncingPrices: false,
            syncPricesError: error,
        }),

    clearPrices: () => set(initialPricesState),

    loadSignatoriesStarted: (merchant: Merchant) =>
        set({
            ...initialAgreementState,
            agreementMerchantKey: getMerchantKey(merchant),
            isLoadingSignatories: true,
        }),

    loadSignatoriesSucceeded: (merchant: Merchant, signatories: RegisteredSignatory[]) =>
        set(state =>
            // A slower response for a merchant whose modal is no longer open must not replace the current list.
            state.agreementMerchantKey === getMerchantKey(merchant)
                ? { signatories, isLoadingSignatories: false }
                : {}
        ),

    loadSignatoriesFailed: (error: ResponseError) =>
        set({
            isLoadingSignatories: false,
            signatoriesError: error,
        }),

    sendAgreementStarted: () =>
        set({
            isSendingAgreement: true,
            agreementSent: false,
            sendAgreementError: undefined,
        }),

    // The merchant comes back with the agreement pending, which the list shows right away.
    sendAgreementSucceeded: (merchant: Merchant) =>
        set(state => ({
            merchants: state.merchants.map(existing =>
                getMerchantKey(existing) === getMerchantKey(merchant) ? merchant : existing
            ),
            isSendingAgreement: false,
            agreementSent: true,
        })),

    sendAgreementFailed: (error: ResponseError) =>
        set({
            isSendingAgreement: false,
            sendAgreementError: error,
        }),

    clearAgreement: () => set(initialAgreementState),

    reset: () => set(initialState),
}));
